import { SagaIterator } from 'redux-saga';
import { call, cancel, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import * as shallowEqual from 'shallowequal';
import * as _ from 'underscore';

import { Messages } from '../../../../codegen/Localize';
import { allItemsExistInRange } from '../../../../common/react/SparseCachedList';
import { fetchHomeContent } from '../../../../home/sagas/HomeApiSaga';
import { scheduleSubspaceNameMatchesSchedule } from '../../../../schedule/utils/ScheduleUtils';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { ListResult } from '../../../../ts/fetchers/ListResult';
import { CollectionsErrorCode } from '../../../../ts/helpers/ErrorCodes';
import { Order } from '../../../../ts/helpers/Order';
import { ServerFilterClauses } from '../../../../ts/helpers/ServerFilterClauses';
import { ExplorableItemDefinitions } from '../../../../ts/models/ContentDefinitions';
import { ExplorableItemType } from '../../../../ts/models/ContentType';
import { FlowOutputStepRunContentType } from '../../../../ts/models/ContentTypes';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { isMetricInfo } from '../../../../metrics/models/IMetricInfo';
import { IRange } from '../../../../ts/models/IRange';
import { IScheduleInfo } from '../../../../schedule/types/IScheduleInfo';
import { ISharedWithMeContentInfo } from '../../../../ts/models/ISharedWithMeContentInfo';
import { ISliceable } from '../../../../ts/models/ISliceable';
import { OrderKeys } from '../../../../ts/models/OrderKeys';
import { Set } from '../../../../ts/models/Set';
import { recentsSpaceDefaultSortOrder } from '../../../../ts/models/sort/RecentsSortOptions';
import { areValid, getDefaultOrderForContentTypesOrSpaceInfo } from '../../../../ts/models/sort/SortOptionDefinitions';
import { Content } from '../../../../ts/resources/Content';
import { Favorites } from '../../../../ts/resources/Favorites';
import { GetContentInFavoritesOrderResource } from '../../../../ts/resources/GetContentInFavoritesOrderResource';
import { Recents } from '../../../../ts/resources/Recents';
import { IResourceQuery } from '../../../../ts/resources/ResourceQuery';
import { Search } from '../../../../ts/resources/Search';
import { ViewRecommendations } from '../../../../recommendations/services/ViewRecommendations';
import { AssetListItem } from '../../../../ts/ServerApi/types/AssetListItem';
import { nonSystemJobFilter } from '../../../../backgroundJob/types/IBackgroundJob';
import { ExploreContentApi_ContentType } from '../../../../ts/ServerApi/types/IContent';
import { IError } from '../../../../ts/ServerApi/types/IError';
import { FilterFieldNames, IFieldFilterClause, IFilterClause, IStringValuedFilterClause, IValuedFieldFilterClause } from '../../../../ts/ServerApi/types/IFilterClause';
import { IOrderClause } from '../../../../ts/ServerApi/types/IOrderClause';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { ClearMixedContentFilters, ContentFilterApplied, ContentTypeFilterSelected, SetContentFilterNoRequestContent, ShowContentTypes, SpaceContentsMissingSliceRequest, SpaceContentsRequest, UpdateFiltersInBothUrlAndState, DraftsFetchInfoRequested } from '../../../actions/AppActionTypes';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { updateSpaceInfoCountAction } from '../../../actions/spaces/ContentCountsActions';
import { clearFilterContent, ShowContentTypesAction } from '../../../actions/spaces/ContentExplorerFilterActions';
import { deselectAll } from '../../../actions/spaces/ContentExplorerSelectionActions';
import { getFilterClausesForTypes, removeFilterClauseFromFilters, containsFilterClause } from '../../../actions/spaces/filters/FilterHelpers';
import { SpaceContentsMissingSliceRequestAction, SpaceContentsRequestAction, spaceContentsRequestFailed, spaceContentsRequestSucceeded, spaceContentsSliceSucceeded } from '../../../actions/spaces/RequestSpaceContentsActions';
import { updateAssetListItems } from '../../../actions/spaces/UpdateAssetListItemsAction';
import { updateMetricsData } from '../../../../metrics/actions/UpdateMetricsActions';
import { selectCurrentlyApplicableContentTypes, selectDefaultContentTypeFilterValues, selectMixedContentFilterClauses, selectSearchText, selectSiteSpaceContentType } from '../../../selectors/ContentFilterSelectors';
import { selectContentFromAppState } from '../../../selectors/ContentSelectors';
import { selectContentSort } from '../../../selectors/ContentSortSelectors';
import { NavLocationSelectors } from '../../../selectors/NavigationStateSelectors';
import { SessionSiteSelectors } from '../../../selectors/SessionInfoSelectors';
import { selectSpaceInfo } from '../../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { collectionOwnerCache } from '../../../spaces/Collections/CollectionOwnersCache';
import { CollectionItemsResourceQuery, CollectionsResourceQuery } from '../../../spaces/Collections/Collections';
import { ICollectionInfo } from '../../../spaces/Collections/ICollectionInfo';
import { FilterMap } from '../../../state/ContentExplorerState';
import { convertSiteContentTypeToContentType, SiteSpaceContentType } from '../../../state/SiteSpaceContentTypeState';
import {
  DataRoleSubspaceName, dataRoleSubspaceNameToExplorableItemType, DatasourceSubspaceName,
  datasourceSubspaceNameToExplorableItemType, GroupSubspaceName, groupSubspaceNameToExplorableItemType,
  isAllCollectionsSpace, isAllGroupsSpace, isAllSchedulesSpace, isAllServerUsersSpace, isAllSitesSpace,
  isBackgroundJobsSpace, isCollectionSpace, isDataRoleSpace, isDatasourceSpace, isFavoritesSpace,
  isFlowSpace, isGroupSpace, isHomeSpace, isProjectSpace, isRecentsSpace, isRecommendationsSpace,
  isScheduleSpace, isSearchSpace, isServerTasksSpace, isSiteSpace, isSiteTasksSpace, isSiteUserContentSubspace,
  isSiteUserSpace, isSiteUsersSpace, isSomeKindaSearchSpace, isUnifiedSearchSpace, isWorkbookSpace,
  ScheduleSubspaceName, scheduleSubspaceNameToExplorableItemType, SearchSubspaceName,
  searchSubspaceNameToExplorableItemType, ServerTasksSubspaceName, serverTasksSubspaceNameToExplorableItemType,
  SiteTasksSubspaceName, siteTasksSubspaceNameToExplorableItemType, SiteUserSubspaceName,
  siteUserSubspaceNameToExplorableItemType, SpaceInfo, TopLevelProjectId, WorkbookSubspaceName,
  workbookSubspaceNameToExplorableItemType,
  isSharedWithMeSpace,
  isMixedContentSpace,
  isSiteUserCollectionsSubspace,
  isSpaceWithDrafts,
  isWorkbookSpaceWithLuid,
  isPersonalSpace
} from '../../../state/SpaceInfo';
import { applyMixedContentFilter, clearMixedContentFilters, filterByContentType, setContentFilterNoRequestContent, updateFiltersInBothUrlAndState } from './FilterContentSagas';
import { clearDraftState, fetchDraftByParentIdRequested } from '../../../../drafts/actions/DraftsActions';
import { fetchDraftsRequested } from '../../../../drafts/actions/DraftsActions';
import { SharedWithMe } from '../../../../sharing/requests/SharedWithMe';

// We've chosen these page sizes via "trial and error" to get a reasonably fast user experience
// when changing sort / filter / content type and scrolling in list and card view. (TFS 841819)
export const PAGESIZE_SLICE: number = 600;
export const PAGESIZE_INITIAL_LOAD: number = 100;

export function* updateQueryParams(contentTypes: ReadonlyArray<string>) {
  const locationService = ServiceLocator.get<ng.ILocationService>('$location');
  const defaultContentTypeFilterValues: ReadonlyArray<ExploreContentApi_ContentType> = yield select(selectDefaultContentTypeFilterValues);
  const defaultContentTypesSet = Set.fromKeyArray(defaultContentTypeFilterValues);
  const newContentTypesSet = Set.fromKeyArray(contentTypes);

  if (defaultContentTypesSet.containsSameItemsAs(newContentTypesSet)) {
    locationService.search('contentType', null);
  } else {
    locationService.search('contentType', contentTypes.join(','));
  }
  // when updating the url outside Angular, $apply is needed to update the window location.
  ServiceLocator.getAngularActionContext().scope.$apply();
}

export namespace InProgressRequestsTracker {
  let inProgressRequestsByStartIndex: { [startIndex: number]: true } = {};

  export function markRequestInProgress(startIndex: number): void {
    inProgressRequestsByStartIndex[startIndex] = true;
  }

  export function isRequestInProgress(startIndex: number): boolean {
    return inProgressRequestsByStartIndex[startIndex];
  }

  export function markRequestComplete(startIndex: number): void {
    delete inProgressRequestsByStartIndex[startIndex];
  }

  export function clear(): void {
    inProgressRequestsByStartIndex = {};
  }
}

export function clearInProgressRequestsForMissingSlices() {
  InProgressRequestsTracker.clear();
}

/**
 * Fetches content for the explorer for the initial slice. Because the page sizes could be different for
 * the initial slice vs. subsequent slices, the initial request is not tracked for de-duping
 * like the missing slice calls are.
 */
export function fetchContentForInitialSlice<T>(resource: ISliceable<T>): ng.IPromise<ListResult<T>> | undefined {
  clearInProgressRequestsForMissingSlices();
  return resource.getSlice(0, PAGESIZE_INITIAL_LOAD);
}

/**
 * Fetches content for the explorer for missing slices.
 * This function keeps track of in-progress requests, so that it doesn't make duplicate calls.
 * If it returns a promise, it made a new request; if it returns undefined, it did not make a request
 * because that request is already in progress.
 */
export function fetchContentForMissingSlice<T>(resource: ISliceable<T>, startIndex: number, pageSize: number): ng.IPromise<ListResult<T>> | undefined {
  if (!InProgressRequestsTracker.isRequestInProgress(startIndex)) {
    InProgressRequestsTracker.markRequestInProgress(startIndex);

    return resource.getSlice(startIndex, pageSize).then(result => {
      InProgressRequestsTracker.markRequestComplete(startIndex);
      return result;
    });
  }
}

export function* getFiltersForContentTypes(projectId: string, contentTypes: ReadonlyArray<ExplorableItemType>) {
  const mixedContentFilter = yield select(selectMixedContentFilterClauses);
  const appStateContentTypes: ReadonlyArray<ExplorableItemType> = yield select(selectCurrentlyApplicableContentTypes);
  const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);

  return getFilterClausesForTypes(
    mixedContentFilter,
    contentTypes,
    appStateContentTypes,
    projectId,
    spaceInfo);
}

export function* getSiteResourceQueryForSiteSpaceContentType(siteSpaceContentType: SiteSpaceContentType) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    if (!isSiteSpace(spaceInfo)) {
      return;
    }

    const resource: IResourceQuery<any> = yield call(
      getProjectOrSiteResourceQueryForContentTypes, [convertSiteContentTypeToContentType(siteSpaceContentType)]
    );

    // The project type on the site page means to get all projects not just the top level projects
    if (siteSpaceContentType === 'project') {
      const filters = removeFilterClauseFromFilters(resource.getFilters(), FilterFieldNames.topLevelProject);
      return resource.withFilters(filters);
    }
    return resource;

  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

/*
 * param orderOverride(optional)- for the pages i.e. home page where storing the order in redux state does not make sense
 */
export function* getRecentsResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, orderOverride?: IOrderClause[]) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));

    const filtersForContentTypes = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    // GetDetailedRecents doesn't support view-core sorts, we will need
    // use a different API depending on the sorts they each support

    if (FeatureFlags.metricsSpaceEnabled ||
      (order.length === 1 && Order.startsWithField(order, recentsSpaceDefaultSortOrder[0].field))) {

      // we don't want to include the actual filter content type for getDetailedRecents
      const recentFilters = filtersForContentTypes.filter((clause: any) => {
        return !(clause.field && clause.field === 'contentType');
      });

      const recents: Recents = ServiceLocator.get<Recents>('Recents');
      return recents.all(contentTypes)
        .withOrder(order)
        .withFilters(recentFilters);

    } else if (contentTypes.length === 1 && contentTypes[0] === 'view') {

      const resource = ExplorableItemDefinitions['view'].getBasicQuery()
        .withFilters(filtersForContentTypes)
        .addFilter(ServerFilterClauses.serverFilterClauses.recent('true'))
        .withOrder(order);

      return resource;
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getFavoritesResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, orderOverride?: IOrderClause[]) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));

    const filtersForContentTypes = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    if (contentTypes.length !== 1) {
      const resource = ServiceLocator.get<Content>('Content');

      if (FeatureFlags.useCollectionsFavorites) {
        return resource.forFavoritesFromCRASS(filtersForContentTypes).withOrder(order);
      }

      return resource.forFavorites(filtersForContentTypes).withOrder(order);

    } else {
      const shownContentType = contentTypes[0];
      let resource = ExplorableItemDefinitions[shownContentType].getBasicQuery()
        .withFilters(filtersForContentTypes)
        .addFilter(ServerFilterClauses.serverFilterClauses.favorite('true'))
        .withOrder(order);

      if (shownContentType === 'project') {
        // we want all projects not just top level ones
        const filters = removeFilterClauseFromFilters(resource.getFilters(), FilterFieldNames.topLevelProject);
        resource = resource.withFilters(filters);
      }

      // Since the regular APIs don't handle the favorite position or date added sort orders
      // use GetContentInFavoritesOrder to handle that
      if (order.length === 1 && (
        Order.startsWithField(order, Order.asc(OrderKeys.position).field) ||
        Order.startsWithField(order, Order.asc(OrderKeys.dateAdded).field))) {
        return new GetContentInFavoritesOrderResource(resource, shownContentType as ExploreContentApi_ContentType, order[0]);
      }

      return resource;
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getSiteUser_ContentSubspace_ResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, orderOverride?: IOrderClause[]) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));

    const filtersForContentTypesInitial: IFilterClause[] = yield call(getFiltersForContentTypes, TopLevelProjectId, contentTypes);
    const filtersForContentTypes = removeFilterClauseFromFilters(filtersForContentTypesInitial, FilterFieldNames.topLevelProject);

    if (contentTypes.length !== 1) {
      const resource = ServiceLocator.get<Content>('Content');

      if (FeatureFlags.useCollectionsFavorites && containsFilterClause(filtersForContentTypes, FilterFieldNames.isFavorite)) {
        const filters = removeFilterClauseFromFilters(filtersForContentTypes, FilterFieldNames.isFavorite);
        filters.push(ServerFilterClauses.serverFilterClauses.owner(spaceInfo.item.id));
        return resource.forFavoritesFromCRASS(filters).withOrder(order);
      }

      return resource.forUser(spaceInfo.item.id)
        .withFilters(filtersForContentTypes)
        .withOrder(order);

    } else {
      const shownContentType = contentTypes[0];
      let resource = ExplorableItemDefinitions[shownContentType].getBasicQuery();

      const ownerFilter: IFilterClause = shownContentType === 'dataalert'
        ? ServerFilterClauses.serverFilterClauses.user(spaceInfo.item.id)
        : ServerFilterClauses.serverFilterClauses.owner(spaceInfo.item.id);

      return resource.withOrder(order)
        .withFilters(filtersForContentTypes)
        .addFilter(ownerFilter);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getSiteUser_ResourceQueryForSpaceTypes(spaceInfo: SpaceInfo) {
  const contentType = siteUserSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SiteUserSubspaceName);

  if (contentType) {
    let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
    const filtersForContentTypesInitial: IFilterClause[] = yield call(getFiltersForContentTypes, TopLevelProjectId, [contentType]);
    const filtersForContentTypes = removeFilterClauseFromFilters(filtersForContentTypesInitial, FilterFieldNames.topLevelProject);
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    const ownerFilter: IFilterClause = spaceInfo.subspaceName === 'alerts'
      ? ServerFilterClauses.serverFilterClauses.user(spaceInfo.item.id)
      : ServerFilterClauses.serverFilterClauses.owner(spaceInfo.item.id);
    return resource.withOrder(order)
      .withFilters(filtersForContentTypes)
      .addFilter(ownerFilter);
  }
}

export function* getAllCollectionsResourceQuery() {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    const filters: IFilterClause[] = yield call(getFiltersForContentTypes, TopLevelProjectId, ['collection']);
    const order: IOrderClause[] = yield call(getOrderClause, ['collection'], spaceInfo);
    return new CollectionsResourceQuery()
      .withOrder(order)
      .withFilters(filters);
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getCollectionResourceQuery() {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    return new CollectionItemsResourceQuery(spaceInfo.item.luid);
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getRecommendationsResourceQuery() {
  try {
    return ViewRecommendations.viewRecommendations();
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getSharedWithMeResourceQuery(contentTypes: ReadonlyArray<ExplorableItemType>,
  orderOverride?: IOrderClause[]) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    // const order: IOrderClause[] = orderOverride || (yield call(getOrderClause, contentTypes, spaceInfo));
    const filtersForContentTypes = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    return SharedWithMe.getSharedWithMe().withFilters(filtersForContentTypes);
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getProjectOrSiteResourceQueryForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);

    if (!(isProjectSpace(spaceInfo) || isSiteSpace(spaceInfo))) {
      return;
    }

    const order: IOrderClause[] = yield call(getOrderClause, contentTypes, spaceInfo);
    const projectId = spaceInfo.item.id;

    const filtersForContentTypes: IFilterClause[] = yield call(getFiltersForContentTypes, spaceInfo.item.id, contentTypes);

    if (contentTypes.length !== 1) {
      const resource = ServiceLocator.get<Content>('Content');

      if (isProjectSpace(spaceInfo) && FeatureFlags.useCollectionsFavorites && containsFilterClause(filtersForContentTypes, FilterFieldNames.isFavorite)) {
        const filters = removeFilterClauseFromFilters(filtersForContentTypes, FilterFieldNames.isFavorite);
        filters.push(ServerFilterClauses.serverFilterClauses.project(projectId));
        return resource.forFavoritesFromCRASS(filters).withOrder(order);
      }

      return resource.forProject(projectId, filtersForContentTypes).withOrder(order);

    } else {
      const shownContentType = contentTypes[0];
      let resource = ExplorableItemDefinitions[shownContentType].getBasicQuery();
      resource = resource.withFilters(filtersForContentTypes);
      return resource.withOrder(order);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getOrderClause(currentContentTypeFilterValues: ExplorableItemType[], spaceInfo: SpaceInfo) {
  const order = yield select(selectContentSort);
  if (!areValid(order, currentContentTypeFilterValues, spaceInfo)) {
    return getDefaultOrderForContentTypesOrSpaceInfo(currentContentTypeFilterValues, spaceInfo);
  }

  return order;
}

export function* getResourceQuery(spaceInfo: SpaceInfo) {
  try {
    if (isWorkbookSpace(spaceInfo)) {

      const contentType = workbookSubspaceNameToExplorableItemType(spaceInfo.subspaceName as WorkbookSubspaceName);
      if (contentType) {
        let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);

        return resource.withOrder(order)
          .withFilters([ServerFilterClauses.serverFilterClauses.workbook(spaceInfo.item.id)]);
      }

    } else if (isProjectSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getProjectOrSiteResourceQueryForContentTypes, contentTypes);

    } else if (isSiteSpace(spaceInfo)) {
      const siteSpaceContentType = yield select(selectSiteSpaceContentType);
      return yield call(getSiteResourceQueryForSiteSpaceContentType, siteSpaceContentType);

    } else if (isDatasourceSpace(spaceInfo)) {
      return yield call(getDatasourceResourceQuery, spaceInfo);

    } else if (isDataRoleSpace(spaceInfo)) {
      return yield call(getDataRoleResourceQuery, spaceInfo);

    } else if (isFavoritesSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getFavoritesResourceQueryForContentTypes, contentTypes);

    } else if (isRecentsSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getRecentsResourceQueryForContentTypes, contentTypes);

    } else if (isRecommendationsSpace(spaceInfo)) {
      return yield call(getRecommendationsResourceQuery);

    } else if (isSomeKindaSearchSpace(spaceInfo)) {
      return yield call(getSearchSpaceResourceQuery, spaceInfo);

    } else if (isSiteUserContentSubspace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getSiteUser_ContentSubspace_ResourceQueryForContentTypes, contentTypes);

    } else if (isSiteUserSpace(spaceInfo)) {
      return yield call(getSiteUser_ResourceQueryForSpaceTypes, spaceInfo);

    } else if (isSiteUsersSpace(spaceInfo)) {
      return yield call(getAllSiteUsersResourceQuery, spaceInfo);

    } else if (isAllServerUsersSpace(spaceInfo)) {
      return yield call(getAllServerUsersResourceQuery, spaceInfo);

    } else if (isSiteTasksSpace(spaceInfo)) {
      return yield call(getSiteTasksResourceQuery, spaceInfo);

    } else if (isAllCollectionsSpace(spaceInfo)) {
      return yield call(getAllCollectionsResourceQuery);

    } else if (isCollectionSpace(spaceInfo)) {
      return yield call(getCollectionResourceQuery);

    } else if (FeatureFlags.sharedWithMeBasicEnabled && isSharedWithMeSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getSharedWithMeResourceQuery, contentTypes);

    } else if (FeatureFlags.personalSpaceEnabled && isPersonalSpace(spaceInfo)) {
      const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
      return yield call(getFavoritesResourceQueryForContentTypes, contentTypes);

    } else if (isFlowSpace(spaceInfo)) {
      if (spaceInfo.subspaceName === 'runHistory') {
        const resource = ExplorableItemDefinitions['flowOutputStepRun'].getBasicQuery();
        const order: IOrderClause[] = yield call(getOrderClause, ['flowOutputStepRun'], spaceInfo);
        const mixedContentFilter = yield select(selectMixedContentFilterClauses);
        const typeOnlyFilters = _.pick(mixedContentFilter, FlowOutputStepRunContentType.filterKeys);
        let typeOnlyFiltersWithDefaultAsArray: IFilterClause[] = _.compact(_.values(typeOnlyFilters));

        return resource.withOrder(order)
          .withFilters(typeOnlyFiltersWithDefaultAsArray)
          .addFilter(ServerFilterClauses.serverFilterClauses.flow(spaceInfo.item.id));
      }
    } else if (isBackgroundJobsSpace(spaceInfo)) {
      return yield call(getBackgroundJobsQuery, spaceInfo);
    } else if (isAllSitesSpace(spaceInfo)) {
      return yield call(getAllSitesResourceQuery, spaceInfo);
    } else if (isAllSchedulesSpace(spaceInfo)) {
      return yield call(getAllSchedulesResourceQuery, spaceInfo);
    } else if (isScheduleSpace(spaceInfo)) {
      return yield call(getScheduleResourceQuery, spaceInfo);
    } else if (isServerTasksSpace(spaceInfo)) {
      return yield call(getServerTasksResourceQuery, spaceInfo);
    } else if (isGroupSpace(spaceInfo)) {
      return yield call(getGroupResourceQuery, spaceInfo);
    } else if (isAllGroupsSpace(spaceInfo)) {
      return yield call(getAllGroupsResourceQuery, spaceInfo);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* getBackgroundJobsQuery(spaceInfo: SpaceInfo) {
  // We have both boolean and string type filters in the background jobs space
  let filters: IFieldFilterClause[] = yield call(getFiltersForContentTypes, spaceInfo.item.id, ['backgroundJob']);

  const isSystemJobsFilterClause = (f: IFieldFilterClause) => {
    return f.field === FilterFieldNames.showSystemJobs;
  };
  const showSystemJobsFilter: IFieldFilterClause | undefined = filters.find(isSystemJobsFilterClause);
  const includeSystemJobs = showSystemJobsFilter
    && (showSystemJobsFilter as IValuedFieldFilterClause<boolean>).value === true;

  // Regardless of truthiness, this filter is a pseudo-filter that shouldn't be in the query
  filters = filters.filter(f => !isSystemJobsFilterClause(f));

  if (includeSystemJobs) {
    // Remove the default filter that filters out any system type job
    filters = filters.filter(f => !shallowEqual(f, nonSystemJobFilter));
  }

  const order: IOrderClause[] = yield call(getOrderClause, ['backgroundJobs'], spaceInfo);
  const resource = ExplorableItemDefinitions['backgroundJob']
    .getBasicQuery()
    .withOrder(order)
    .withFilters(filters);
  return resource;
}

export function* getDatasourceResourceQuery(spaceInfo: SpaceInfo) {
  const contentType = datasourceSubspaceNameToExplorableItemType(spaceInfo.subspaceName as DatasourceSubspaceName);
  if (contentType) {
    let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    return resource.withOrder(order)
      .withFilters([ServerFilterClauses.serverFilterClauses.datasource(spaceInfo.item.id)]);
  }
}

export function* getDataRoleResourceQuery(spaceInfo: SpaceInfo) {
  const contentType = dataRoleSubspaceNameToExplorableItemType(spaceInfo.subspaceName as DataRoleSubspaceName);
  if (contentType) {
    let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    return resource.withOrder(order)
      .withFilters([ServerFilterClauses.serverFilterClauses.flow(spaceInfo.item.id)]); // TODO (TFSID 940582): Use the correct set of filters for this space
  }
}

export function* buildCrassSearch() {
  let search = new Search();

  const searchText = yield select(selectSearchText);
  if (searchText) {
    search = search.search(searchText);
  }

  const orderClauses = yield select(selectContentSort);
  if (orderClauses && orderClauses.length > 0) {
    search = search.withOrder(orderClauses);
  }

  return search;
}

export function* getSearchSpaceResourceQuery(spaceInfo: SpaceInfo) {
  if (FeatureFlags.crassSearch) {
    return yield call(buildCrassSearch);
  }

  const contentType = searchSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SearchSubspaceName);
  if (contentType) {
    let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
    if (contentType === 'view' || contentType === 'workbook') {
      const extraParams = resource.getExtraParams();
      resource = resource.withExtraParams({...extraParams, forSearchPage: true});
    }
    const filtersForContentTypes: IFilterClause[] = yield call(getFiltersForContentTypes, spaceInfo.item.id, [contentType]);
    resource = resource.withFilters(filtersForContentTypes);
    if (contentType === 'project') {
      // we want to search across all projects not just top level ones
      const filters = removeFilterClauseFromFilters(resource.getFilters(), FilterFieldNames.topLevelProject);
      resource = resource.withFilters(filters);
    }
    const order: IOrderClause[] = yield call(getOrderClause, [], spaceInfo);
    return resource.withOrder(order);
  }
}

export function* getAllSiteUsersResourceQuery(spaceInfo: SpaceInfo) {
  const contentType: ExplorableItemType = 'siteuser';
  let resource = ExplorableItemDefinitions[contentType].getBasicQuery();

  const filters = yield call(getFiltersForContentTypes, TopLevelProjectId, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getAllServerUsersResourceQuery(spaceInfo: SpaceInfo) {
  const contentType: ExplorableItemType = 'serveruser';
  let resource = ExplorableItemDefinitions[contentType].getBasicQuery();

  const filters = yield call(getFiltersForContentTypes, TopLevelProjectId, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getSiteTasksResourceQuery(spaceInfo: SpaceInfo) {
  const contentType = siteTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SiteTasksSubspaceName);
  if (contentType) {
    const filtersForContentTypesInitial: IFilterClause[] = yield call(getFiltersForContentTypes, null, [contentType]);
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);

    const siteId: string = yield select(SessionSiteSelectors.siteId);
    const siteIdFilter: IFilterClause = ServerFilterClauses.serverFilterClauses.siteId(siteId);

    const resource = ExplorableItemDefinitions[contentType]
      .getBasicQuery()
      .withOrder(order)
      .withFilters(filtersForContentTypesInitial)
      .addFilter(siteIdFilter);
    return resource;
  }
}

export function* getServerTasksResourceQuery(spaceInfo: SpaceInfo) {
  const contentType = serverTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ServerTasksSubspaceName);
  if (contentType) {
    const filtersForContentTypesInitial: IFilterClause[] = yield call(getFiltersForContentTypes, null, [contentType]);
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);

    const resource = ExplorableItemDefinitions[contentType]
      .getBasicQuery()
      .withOrder(order)
      .withFilters(filtersForContentTypesInitial);
    return resource;
  }
}

export function* getAllSitesResourceQuery(spaceInfo: SpaceInfo) {
  const contentType: ExplorableItemType = 'site';
  const resource = ExplorableItemDefinitions[contentType].getBasicQuery();
  const filtersForContentTypes: IFilterClause[] = yield call(getFiltersForContentTypes, TopLevelProjectId, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filtersForContentTypes);
}

export function* getAllSchedulesResourceQuery(spaceInfo: SpaceInfo) {
  const contentType: ExplorableItemType = 'schedule';
  let resource = ExplorableItemDefinitions[contentType].getBasicQuery();

  const filters = _.compact(_.values(ExplorableItemDefinitions[contentType].defaultFilters('')));
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getAllGroupsResourceQuery(spaceInfo: SpaceInfo) {
  const contentType: ExplorableItemType = 'group';
  let resource = ExplorableItemDefinitions[contentType].getBasicQuery();

  const filters = yield call(getFiltersForContentTypes, null, [contentType]);
  const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
  return resource
    .withOrder(order)
    .withFilters(filters);
}

export function* getScheduleResourceQuery(spaceInfo: SpaceInfo) {
  const explorableItemType = scheduleSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ScheduleSubspaceName);
  if (explorableItemType) {
    const scheduledAction = (spaceInfo.item as IScheduleInfo).scheduledAction;
    if (!scheduleSubspaceNameMatchesSchedule(spaceInfo.subspaceName as ScheduleSubspaceName, scheduledAction)) {
      return;
    }

    let resource = ExplorableItemDefinitions[explorableItemType].getBasicQuery();

    const filters = _.compact(_.values(ExplorableItemDefinitions[explorableItemType].defaultFilters('')));
    const inServerArea = yield select(NavLocationSelectors.inServerArea);
    if (!inServerArea) {
      const siteId: string = yield select(SessionSiteSelectors.siteId);
      const siteIdFilter: IFilterClause = ServerFilterClauses.serverFilterClauses.siteId(siteId);
      filters.push(siteIdFilter);
    }

    const scheduleIdFilter = ServerFilterClauses.serverFilterClauses.schedule(spaceInfo.item.id);
    filters.push(scheduleIdFilter);

    const order: IOrderClause[] = yield call(getOrderClause, [explorableItemType], spaceInfo);
    return resource
      .withOrder(order)
      .withFilters(filters);
  }
}

export function* getGroupResourceQuery(spaceInfo: SpaceInfo) {
  const contentType = groupSubspaceNameToExplorableItemType(spaceInfo.subspaceName as GroupSubspaceName);
  if (contentType) {
    let resource = ExplorableItemDefinitions[contentType].getBasicQuery();
    const order: IOrderClause[] = yield call(getOrderClause, [contentType], spaceInfo);
    const filters = yield call(getFiltersForContentTypes, null, [contentType]);
    filters.push(ServerFilterClauses.serverFilterClauses.inGroup(spaceInfo.item.id));
    return resource.withOrder(order)
      .withFilters(filters);
  }
}

export function* processRequestSpaceContent(action: SpaceContentsRequestAction) {
  const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);

  try {
    if (isHomeSpace(spaceInfo)) {
      // Clear search filters before fetching content
      yield put(clearFilterContent());
      yield call(fetchHomeContent);

    } else {
      // on the search space if there is no search term then clear any items and don't get a resource query
      if (isSomeKindaSearchSpace(spaceInfo)) {
        const mixedContentFilter: FilterMap = yield select(selectMixedContentFilterClauses);
        if (_.isEmpty(mixedContentFilter) || !mixedContentFilter.search || (mixedContentFilter.search as IStringValuedFilterClause).value.length === 0) {
          const noContent: ListResult<IContentInfo> = ListResult.empty<IContentInfo>();
          yield put(spaceContentsRequestSucceeded(noContent));
          return;
        }
      }

      const resource = yield call(getResourceQuery, spaceInfo);

      yield put(deselectAll());

      // for cases in which there is no content to fetch,
      // resource can be undefined (I.E. NLP)
      if (!_.isUndefined(resource)) {
        // need to clear the drafts state before rendering workbook space to prevent draft banner from loading before
        // fetch drafts request is completed
        if (FeatureFlags.webAuthoringAutoSaveEnabled && isWorkbookSpace(spaceInfo)) {
          yield put(clearDraftState());
        }

        let content: ListResult<IContentInfo>;

        // If we are in the crass search results prototype, a different type of resource is used.
        if (isUnifiedSearchSpace(spaceInfo)) {
          content = yield call(fetchMissingSliceFromCrass, resource, 0, PAGESIZE_INITIAL_LOAD);
        } else {
          content = yield call(fetchContentForInitialSlice, resource);
        }

        if (content) {
          handleGetContentErrors(spaceInfo, content.errors);
          yield put(spaceContentsRequestSucceeded(content));

          // update individual count for mixed content space by setting the specific content type as a subspacename
          if (isMixedContentSpace(spaceInfo)) {
            const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
            if (contentTypes.length === 1) {
              const thisSpaceInfo: SpaceInfo = { ...spaceInfo, subspaceName: contentTypes[0] };
              yield put(updateSpaceInfoCountAction(thisSpaceInfo, content.total));
            }
          } else {
            yield put(updateSpaceInfoCountAction(spaceInfo, content.total));
          }

          updateOwnerCache(spaceInfo, content);

          if (isFavoritesSpace(spaceInfo)) {
            const contentTypes = yield select(selectCurrentlyApplicableContentTypes);
            const assetListItems: ListResult<AssetListItem> = yield call(getFavoritesForContentTypes, contentTypes);
            if (assetListItems) {
              yield put(updateAssetListItems(assetListItems.items));
            }
          }

          if (FeatureFlags.webAuthoringAutoSaveEnabled) {
            if (isWorkbookSpaceWithLuid(spaceInfo)) {
              yield put(fetchDraftByParentIdRequested(spaceInfo.spaceType, spaceInfo.item.luid));
            } else if (isSpaceWithDrafts(spaceInfo)) {
              yield put(fetchDraftsRequested(spaceInfo.spaceType));
            }
          }
        }
      }
    }
  } catch (e) {
    yield put(spaceContentsRequestFailed());
    const errorMessage = getErrorMessage(spaceInfo, e);
    yield put(requestFailed(e, errorMessage));
  }
}

export function handleGetContentErrors(spaceInfo: SpaceInfo, errors: IError[]) {
  const toasterService = ToasterService.getInstance();
  errors && errors.forEach(error => {
    if (error.code === CollectionsErrorCode.UNABLE_TO_FETCH_FROM_CONTENT_SERVICE) {
      if (isAllCollectionsSpace(spaceInfo) || isSiteUserCollectionsSubspace(spaceInfo)) {
        toasterService.pop({
          type: ToastType.Warning,
          text: Messages.unableToFetchAllCollectionInfo(),
        });
      } else if (isCollectionSpace(spaceInfo)) {
        toasterService.pop({
          type: ToastType.Warning,
          text: Messages.unableToFetchAllCollectionItems(),
        });
      }
    }
  });
}

function getErrorMessage(spaceInfo: SpaceInfo, e: Error): string {
  if (isSearchSpace(spaceInfo)) {
    switch (spaceInfo.subspaceName) {
      case 'tables':
        return Messages.catalogingTablesSearchErrorMessage();
      case 'databases':
      case 'databasesWithCrass':
        return Messages.catalogingDatabasesSearchErrorMessage();
    }
  }

  return Messages.errorUnknown();
}

function getFavoritesForContentTypes(contentTypes: ExploreContentApi_ContentType[]): ng.IPromise<ListResult<AssetListItem>> {
  const resource = ServiceLocator.get<Favorites>('Favorites');
  // TODO: All favorites code depends on fetch size of 1000 items. Should use consistent named constant for this.
  return resource.allForTypesAndSort(contentTypes, [])
    .getSlice(0, 1000);
}

/**
 * Missing slices are valid only for the current explorer query, which is set up via various actions
 * (those that set the order, filters, etc. in the store) but initiated by a SpaceContentsRequest
 * (that is where the current space contents get cleared out and re-populated with the contents of
 * the new query.
 *
 * Hence, if we ever get a SpaceContentsRequest, all pending missing slice requests must be cancelled,
 * otherwise slices from the old query would be inserted into the new query's results.
 */
export function* processRequestSpaceContentMissingSliceWithCancellation(pageSize: number, action: SpaceContentsMissingSliceRequestAction) {
  const missingSliceTask = yield fork(processRequestSpaceContentMissingSliceInner, pageSize, action);

  yield take(SpaceContentsRequest);
  yield cancel(missingSliceTask);
}

export function* processRequestSpaceContentMissingSliceInner(pageSize: number, action: SpaceContentsMissingSliceRequestAction) {
  const itemRange: IRange = { start: action.start, end: action.end };

  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const resource = yield call(getResourceQuery, spaceInfo);

    const pageIndexOfFirstItem = Math.floor(itemRange.start / pageSize);
    const pageIndexOfLastItem = Math.floor(itemRange.end / pageSize);

    let items: ReadonlyArray<IContentInfo> = yield select(selectContentFromAppState);

    if (FeatureFlags.metricsSpaceEnabled) {
      if (isSharedWithMeSpace(spaceInfo)) {
        items = items.slice(itemRange.start, itemRange.end).filter((item) => {
          return !(item as ISharedWithMeContentInfo).isAccessDenied;
        });
      }

      const metrics = items.slice(itemRange.start, itemRange.end).filter(isMetricInfo);
      if (metrics.length > 0) {
        yield put(updateMetricsData(metrics));
      }
    }

    for (let pageIndex = pageIndexOfFirstItem; pageIndex <= pageIndexOfLastItem; pageIndex++) {

      const startIndexToFetch = pageSize * pageIndex;
      const endIndexToFetch = startIndexToFetch + pageSize - 1;

      if (!allItemsExistInRange(items, startIndexToFetch, endIndexToFetch)) {
        // If we are in the crass search results prototype, a different type of resource is used.
        let content: ListResult<IContentInfo>;
        if (isUnifiedSearchSpace(spaceInfo)) {
          content = yield call(fetchMissingSliceFromCrass, resource, startIndexToFetch, pageSize);
        } else {
          content = yield call(fetchContentForMissingSlice, resource, startIndexToFetch, pageSize);
        }


        if (content) {
          yield put(spaceContentsSliceSucceeded(startIndexToFetch, content));

          updateOwnerCache(spaceInfo, content);
        }
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// CRASS Prototype Code
function fetchMissingSliceFromCrass(resource: Search, startIndex: number, pageSize: number): ng.IPromise<ListResult<IContentInfo>> {
  const pageIndex = Math.floor(startIndex / pageSize);
  return resource.page(pageIndex).limit(pageSize).getResults().then(searchResult => {
    const content: ListResult<IContentInfo> = {
      index: startIndex,
      total: searchResult.total,
      items: [...searchResult.items]
    };
    return content;
  });
}

function updateOwnerCache(spaceInfo: SpaceInfo, content: ListResult<IContentInfo<ExplorableItemType>>) {
  if (isAllCollectionsSpace(spaceInfo) || isSiteUserCollectionsSubspace(spaceInfo)) {
    collectionOwnerCache.updateCollectionOwners(content.items as unknown as ICollectionInfo[], 'collections');
  } else if (isCollectionSpace(spaceInfo)) {
    collectionOwnerCache.updateCollectionOwners([spaceInfo.item as ICollectionInfo], 'collection');
  }
}

export function* processShowContentTypes(action: ShowContentTypesAction) {
  try {
    yield call(updateQueryParams, action.contentTypes);
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* rootMixedContentSaga(): SagaIterator {
  yield takeLatest(SpaceContentsRequest, processRequestSpaceContent);
  yield takeLatest(ContentTypeFilterSelected, filterByContentType);
  yield takeLatest(ShowContentTypes, processShowContentTypes);
  yield takeEvery(SpaceContentsMissingSliceRequest, processRequestSpaceContentMissingSliceWithCancellation, PAGESIZE_SLICE);
  yield takeLatest(ContentFilterApplied, applyMixedContentFilter);
  yield takeLatest(SetContentFilterNoRequestContent, setContentFilterNoRequestContent);
  yield takeLatest(ClearMixedContentFilters, clearMixedContentFilters);
  yield takeEvery(UpdateFiltersInBothUrlAndState, updateFiltersInBothUrlAndState);
}
